import { useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import { useSnackbar } from '../../components/SnackbarProvider';
import axiosInstance from '../../lib/axios';
import type { Customer, CustomerType, CreateCustomerDto } from '../../types/customer';
import { CustomerType as CustomerTypeEnum } from '../../types/customer';
import type { PricingTier } from '../../types/b2b';

interface CustomerFormModalProps {
  open: boolean;
  onClose: () => void;
  customer?: Customer | null;
}

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  isPrimary: boolean;
}

interface FormData {
  type: CustomerType;
  // Individual fields
  firstName: string;
  lastName: string;
  // Business fields
  companyName: string;
  defaultTierId: string;
  contacts: ContactFormData[];
  isApproved: boolean;
  // Common fields
  email: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  latitude: string;
  longitude: string;
  preferDelivery: boolean;
  preferPickup: boolean;
  deliveryInstructions: string;
  notes: string;
}

const defaultContactValues: ContactFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  role: '',
  isPrimary: false,
};

const defaultValues: FormData = {
  type: CustomerTypeEnum.INDIVIDUAL,
  firstName: '',
  lastName: '',
  companyName: '',
  defaultTierId: '',
  contacts: [],
  isApproved: true,
  email: '',
  phone: '',
  street: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'DZ',
  latitude: '',
  longitude: '',
  preferDelivery: false,
  preferPickup: false,
  deliveryInstructions: '',
  notes: '',
};

const fetchPricingTiers = async (): Promise<PricingTier[]> => {
  const response = await axiosInstance.get('/pricing-tiers');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

export const CustomerFormModal = ({ open, onClose, customer }: CustomerFormModalProps) => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const isEditing = !!customer;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues,
  });

  const { fields: contactFields, append: appendContact, remove: removeContact } = useFieldArray({
    control,
    name: 'contacts',
  });

  const watchedType = watch('type');

  // Fetch pricing tiers for business customers
  const { data: pricingTiers, isLoading: tiersLoading } = useQuery({
    queryKey: ['pricing-tiers'],
    queryFn: fetchPricingTiers,
    enabled: open && watchedType === CustomerTypeEnum.BUSINESS,
  });

  useEffect(() => {
    if (customer) {
      reset({
        type: customer.type,
        firstName: customer.firstName || '',
        lastName: customer.lastName || '',
        companyName: customer.companyName || '',
        defaultTierId: customer.defaultTierId || '',
        contacts: customer.contacts?.map(c => ({
          firstName: c.firstName,
          lastName: c.lastName,
          email: c.email || '',
          phone: c.phone || '',
          role: c.role || '',
          isPrimary: c.isPrimary,
        })) || [],
        isApproved: customer.isApproved,
        email: customer.email || '',
        phone: customer.phone || '',
        street: customer.street || '',
        city: customer.city || '',
        state: customer.state || '',
        postalCode: customer.postalCode || '',
        country: customer.country || 'DZ',
        latitude: customer.latitude?.toString() || '',
        longitude: customer.longitude?.toString() || '',
        preferDelivery: customer.preferDelivery || false,
        preferPickup: customer.preferPickup || false,
        deliveryInstructions: customer.deliveryInstructions || '',
        notes: customer.notes || '',
      });
    } else {
      reset(defaultValues);
    }
  }, [customer, reset]);

  // Handle type change - reset type-specific fields
  const handleTypeChange = (newType: CustomerType) => {
    setValue('type', newType);
    if (newType === CustomerTypeEnum.INDIVIDUAL) {
      setValue('companyName', '');
      setValue('defaultTierId', '');
      setValue('contacts', []);
      setValue('isApproved', true);
    } else {
      setValue('firstName', '');
      setValue('lastName', '');
    }
  };

  const createMutation = useMutation({
    mutationFn: async (data: CreateCustomerDto) => {
      const response = await axiosInstance.post('/customers', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      showMessage('Client créé avec succès', 'success');
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la création';
      showMessage(message, 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axiosInstance.patch(`/customers/${customer?.id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      showMessage('Client mis à jour avec succès', 'success');
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la mise à jour';
      showMessage(message, 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await axiosInstance.delete(`/customers/${customer?.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      showMessage('Client supprimé avec succès', 'success');
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la suppression';
      showMessage(message, 'error');
    },
  });

  const onSubmit = async (data: FormData) => {
    // Clean up empty optional fields
    const cleanData: any = {
      type: data.type,
      email: data.email,
    };

    // Add type-specific fields
    if (data.type === CustomerTypeEnum.INDIVIDUAL) {
      cleanData.firstName = data.firstName;
      cleanData.lastName = data.lastName;
    } else {
      cleanData.companyName = data.companyName;
      if (data.defaultTierId) {
        cleanData.defaultTierId = data.defaultTierId;
      }
      cleanData.isApproved = data.isApproved;
      // Add contacts if any
      if (data.contacts && data.contacts.length > 0) {
        cleanData.contacts = data.contacts.map(c => ({
          firstName: c.firstName,
          lastName: c.lastName,
          email: c.email || undefined,
          phone: c.phone || undefined,
          role: c.role || undefined,
          isPrimary: c.isPrimary,
        })).filter(c => c.firstName && c.lastName);
      }
    }

    // Add common optional fields if provided
    if (data.phone) cleanData.phone = data.phone;
    if (data.street) cleanData.street = data.street;
    if (data.city) cleanData.city = data.city;
    if (data.state) cleanData.state = data.state;
    if (data.postalCode) cleanData.postalCode = data.postalCode;
    if (data.country) cleanData.country = data.country;
    if (data.latitude) cleanData.latitude = parseFloat(data.latitude);
    if (data.longitude) cleanData.longitude = parseFloat(data.longitude);
    cleanData.preferDelivery = data.preferDelivery;
    cleanData.preferPickup = data.preferPickup;
    if (data.deliveryInstructions) cleanData.deliveryInstructions = data.deliveryInstructions;
    if (data.notes) cleanData.notes = data.notes;

    if (isEditing) {
      // Remove type from update data (type is immutable)
      const { type, ...updateData } = cleanData;
      await updateMutation.mutateAsync(updateData);
    } else {
      await createMutation.mutateAsync(cleanData);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Voulez-vous vraiment supprimer ce client ?')) {
      await deleteMutation.mutateAsync();
    }
  };

  const handleAddContact = () => {
    appendContact({ ...defaultContactValues });
  };

  const isLoading = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">
              {isEditing ? 'Modifier le client' : 'Nouveau client'}
            </Typography>
            {isEditing && customer && (
              <Chip
                icon={customer.type === CustomerTypeEnum.BUSINESS ? <BusinessIcon /> : <PersonIcon />}
                label={customer.type === CustomerTypeEnum.BUSINESS ? 'Entreprise' : 'Particulier'}
                size="small"
                color={customer.type === CustomerTypeEnum.BUSINESS ? 'info' : 'primary'}
                variant="outlined"
              />
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Type Selector - only for new customers */}
            {!isEditing && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Type de client
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Type de client *</InputLabel>
                        <Select
                          {...field}
                          label="Type de client *"
                          onChange={(e) => {
                            field.onChange(e);
                            handleTypeChange(e.target.value as CustomerType);
                          }}
                          disabled={isLoading}
                        >
                          <MenuItem value={CustomerTypeEnum.INDIVIDUAL}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <PersonIcon fontSize="small" />
                              Particulier
                            </Box>
                          </MenuItem>
                          <MenuItem value={CustomerTypeEnum.BUSINESS}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <BusinessIcon fontSize="small" />
                              Entreprise (B2B)
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </>
            )}

            {/* Individual-specific fields */}
            {watchedType === CustomerTypeEnum.INDIVIDUAL && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: isEditing ? 0 : 2 }}>
                    Informations personnelles
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{ required: watchedType === CustomerTypeEnum.INDIVIDUAL ? 'Le prénom est requis' : false }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Prénom"
                        fullWidth
                        required
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                        disabled={isLoading}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="lastName"
                    control={control}
                    rules={{ required: watchedType === CustomerTypeEnum.INDIVIDUAL ? 'Le nom est requis' : false }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Nom"
                        fullWidth
                        required
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                        disabled={isLoading}
                      />
                    )}
                  />
                </Grid>
              </>
            )}

            {/* Business-specific fields */}
            {watchedType === CustomerTypeEnum.BUSINESS && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: isEditing ? 0 : 2 }}>
                    Informations de l'entreprise
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="companyName"
                    control={control}
                    rules={{ required: watchedType === CustomerTypeEnum.BUSINESS ? 'Le nom de l\'entreprise est requis' : false }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Nom de l'entreprise"
                        fullWidth
                        required
                        error={!!errors.companyName}
                        helperText={errors.companyName?.message}
                        disabled={isLoading}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="defaultTierId"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth disabled={isLoading || tiersLoading}>
                        <InputLabel>Niveau de tarification</InputLabel>
                        <Select
                          {...field}
                          label="Niveau de tarification"
                        >
                          <MenuItem value="">
                            <em>Aucun</em>
                          </MenuItem>
                          {pricingTiers
                            ?.filter((tier) => tier.isActive)
                            .map((tier) => (
                              <MenuItem key={tier.id} value={tier.id}>
                                {tier.tierName.toUpperCase()} -{' '}
                                {tier.discountType === 'percentage'
                                  ? `${tier.discountValue}%`
                                  : `${tier.discountValue} DZD`}{' '}
                                de réduction
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="isApproved"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={field.onChange}
                            disabled={isLoading}
                          />
                        }
                        label="Client approuvé"
                      />
                    )}
                  />
                </Grid>

                {/* Business Contacts Section */}
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Contacts de l'entreprise (optionnel)
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddContact}
                      disabled={isLoading}
                    >
                      Ajouter un contact
                    </Button>
                  </Box>
                </Grid>

                {contactFields.map((contactField, index) => (
                  <Grid item xs={12} key={contactField.id}>
                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="body2" color="text.secondary">
                          Contact {index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeContact(index)}
                          disabled={isLoading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name={`contacts.${index}.firstName`}
                            control={control}
                            rules={{ required: 'Le prénom est requis' }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Prénom"
                                fullWidth
                                size="small"
                                required
                                error={!!errors.contacts?.[index]?.firstName}
                                helperText={errors.contacts?.[index]?.firstName?.message}
                                disabled={isLoading}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name={`contacts.${index}.lastName`}
                            control={control}
                            rules={{ required: 'Le nom est requis' }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Nom"
                                fullWidth
                                size="small"
                                required
                                error={!!errors.contacts?.[index]?.lastName}
                                helperText={errors.contacts?.[index]?.lastName?.message}
                                disabled={isLoading}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name={`contacts.${index}.email`}
                            control={control}
                            rules={{
                              pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: 'Email invalide',
                              },
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Email"
                                fullWidth
                                size="small"
                                type="email"
                                error={!!errors.contacts?.[index]?.email}
                                helperText={errors.contacts?.[index]?.email?.message}
                                disabled={isLoading}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name={`contacts.${index}.phone`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Téléphone"
                                fullWidth
                                size="small"
                                disabled={isLoading}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name={`contacts.${index}.role`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Rôle"
                                fullWidth
                                size="small"
                                placeholder="Ex: Directeur des achats"
                                disabled={isLoading}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name={`contacts.${index}.isPrimary`}
                            control={control}
                            render={({ field }) => (
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={field.value}
                                    onChange={field.onChange}
                                    disabled={isLoading}
                                    size="small"
                                  />
                                }
                                label="Contact principal"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                ))}
              </>
            )}

            {/* Contact Information - common for both types */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Contact
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: 'L\'email est requis',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Email invalide',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    fullWidth
                    required
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Téléphone"
                    fullWidth
                    placeholder="+213..."
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            {/* Address Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Adresse
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="street"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Rue"
                    fullWidth
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Ville"
                    fullWidth
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Wilaya"
                    fullWidth
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="postalCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Code postal"
                    fullWidth
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Pays"
                    fullWidth
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            {/* GPS Location */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Localisation GPS (optionnel)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="latitude"
                control={control}
                rules={{
                  pattern: {
                    value: /^-?([0-8]?[0-9]|90)(\.[0-9]+)?$/,
                    message: 'Latitude invalide (-90 à 90)',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Latitude"
                    fullWidth
                    placeholder="36.7538"
                    error={!!errors.latitude}
                    helperText={errors.latitude?.message}
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="longitude"
                control={control}
                rules={{
                  pattern: {
                    value: /^-?((1[0-7][0-9])|([0-9]?[0-9]))(\.[0-9]+)?$/,
                    message: 'Longitude invalide (-180 à 180)',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Longitude"
                    fullWidth
                    placeholder="3.0588"
                    error={!!errors.longitude}
                    helperText={errors.longitude?.message}
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            {/* Delivery Preferences */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Préférences de livraison
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="preferDelivery"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={field.onChange}
                        disabled={isLoading}
                      />
                    }
                    label="Préfère la livraison"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="preferPickup"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={field.onChange}
                        disabled={isLoading}
                      />
                    }
                    label="Préfère le retrait"
                  />
                )}
              />
            </Grid>

            {/* Delivery Instructions */}
            <Grid item xs={12}>
              <Controller
                name="deliveryInstructions"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Instructions de livraison"
                    fullWidth
                    multiline
                    rows={2}
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Notes internes
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notes"
                    fullWidth
                    multiline
                    rows={3}
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          {isEditing && (
            <Button
              color="error"
              onClick={handleDelete}
              disabled={isLoading}
              sx={{ mr: 'auto' }}
            >
              Supprimer
            </Button>
          )}
          <Button onClick={onClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
          >
            {isEditing ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
